import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { industries } from '@/data/industries';

export default function Industries() {
  useEffect(() => { document.title = 'Industries We Serve | NKP VENTURES'; }, []);

  return (
    <div>
      {/* ── Hero ── White/light, distinct from Services */}
      <section className="relative pt-32 pb-24 bg-white overflow-hidden border-b border-[#E5E7EB]">
        {/* Decorative large background text */}
        <div
          className="absolute right-0 top-0 bottom-0 flex items-center pointer-events-none select-none"
          aria-hidden="true"
        >
          <span
            className="font-serif font-bold text-[#F0F4F8] leading-none"
            style={{ fontSize: 'clamp(14rem, 22vw, 22rem)', letterSpacing: '-0.06em' }}
          >10</span>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-2.5 mb-8">
              <span className="w-5 h-px bg-[#0E5A57]" />
              <span className="overline-label text-[#0E5A57]">Industries We Serve</span>
            </div>
            <h1 className="font-serif font-bold text-[#153E5C] leading-[1.05] mb-6" style={{ fontSize: 'clamp(2.6rem, 6vw, 4.2rem)' }}>
              Deep sector expertise.
              <br />
              <span className="text-gradient-teal">Across ten industries.</span>
            </h1>
            <p className="text-lg text-[#6B7280] font-sans leading-relaxed max-w-2xl mb-10">
              NKP VENTURES brings sector-appropriate knowledge, compliance awareness, and process expertise to every engagement — shaped by the real operational demands of each industry.
            </p>
            <div className="flex flex-wrap gap-2">
              {industries.slice(0, 5).map(ind => (
                <Link
                  key={ind.slug}
                  to={`/industries/${ind.slug}`}
                  className="text-xs font-medium text-[#0E5A57] bg-teal-50 hover:bg-[#0E5A57] hover:text-white border border-teal-100 hover:border-[#0E5A57] px-4 py-2 rounded-full transition-all duration-200"
                >
                  {ind.shortTitle}
                </Link>
              ))}
              <span className="text-xs text-[#9CA3AF] px-4 py-2">+ 5 more</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Industries Grid ── */}
      <section className="py-24 lg:py-32 bg-[#F7FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="flex items-end justify-between mb-16">
              <div>
                <p className="overline-label text-[#0E5A57] mb-4">All Industries</p>
                <h2 className="font-serif text-4xl lg:text-5xl font-bold text-[#153E5C] leading-tight">
                  Your sector.<br />Our expertise.
                </h2>
              </div>
              <Link
                to="/contact"
                className="hidden lg:inline-flex items-center gap-2 text-[#0E5A57] hover:text-[#2E7D7A] font-semibold text-sm transition-colors duration-200 group"
              >
                Discuss your sector <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {industries.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <Reveal key={ind.id} delay={i * 0.07}>
                  <Link
                    to={`/industries/${ind.slug}`}
                    data-testid={`industry-card-${ind.slug}`}
                    className="group flex flex-col p-7 bg-white border border-[#E5E7EB] rounded-2xl hover:bg-[#153E5C] hover:border-[#153E5C] hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="w-12 h-12 bg-teal-50 border border-teal-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-white/15 group-hover:border-white/20 transition-all duration-300">
                      <Icon className="w-6 h-6 text-[#0E5A57] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="font-serif text-base font-bold text-[#153E5C] group-hover:text-white mb-2 leading-snug transition-colors duration-300">
                      {ind.title}
                    </h3>
                    <p className="text-xs text-[#9CA3AF] group-hover:text-white/55 leading-relaxed flex-1 transition-colors duration-300 line-clamp-2">
                      {ind.heroSub}
                    </p>
                    <div className="mt-5 flex items-center gap-1.5 text-xs font-semibold text-[#0E5A57] group-hover:text-teal-300 transition-colors duration-300">
                      Explore <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform duration-200" />
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Cross-industry note ── */}
      <section className="py-20 bg-white border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="overline-label text-[#0E5A57] mb-4">Sector-Appropriate Standards</p>
                <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#153E5C] mb-6 leading-tight">
                  Industry context shapes everything we do.
                </h2>
                <p className="text-[#6B7280] leading-relaxed font-sans mb-8">
                  Every industry has different operational norms, compliance expectations, and communication standards. NKP VENTURES brings sector-specific awareness to every engagement — ensuring the support we provide is appropriate for the environment our clients operate in.
                </p>
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 text-[#0E5A57] font-semibold text-sm hover:gap-3 transition-all duration-200"
                >
                  View our service portfolio <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {['Compliance-aware operations', 'Sector-specific process knowledge', 'Industry communication standards', 'Confidentiality at every level'].map(point => (
                  <div key={point} className="p-5 bg-[#F7FAFC] border border-[#E5E7EB] rounded-xl">
                    <div className="w-6 h-0.5 bg-gradient-to-r from-[#0E5A57] to-[#D4AF37] rounded mb-3" />
                    <p className="text-sm text-[#374151] font-sans leading-snug font-medium">{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-28 bg-[#153E5C]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="overline-label text-teal-400 mb-4">Don't See Your Sector?</p>
                <h2 className="font-serif text-4xl font-bold text-white mb-4 leading-tight">
                  We support businesses<br />across many sectors.
                </h2>
                <p className="text-white/60 font-sans leading-relaxed max-w-lg">
                  If your industry isn't listed, get in touch. We'll discuss your specific operational context and how NKP VENTURES can support you.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#153E5C] font-bold px-9 py-4 rounded-full transition-all duration-200 group"
                >
                  Discuss Your Needs
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
